import { Link } from 'react-router-dom'
import { ArrowLeft, Target, Zap, Users } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { styles as imagePageStyles } from '@/styles/ImagePage.stylex'
import { controlNetStyles } from '@/styles/ControlNet.stylex'

export default function ControlNetPage() {
    return (
        <div {...stylex.props(imagePageStyles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(imagePageStyles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(imagePageStyles.heroSection)}>
                <div {...stylex.props(imagePageStyles.heroBackground)}>
                    <img
                        src="https://cdn.aibrush.co/docs/images/controlnet/controlnet-hero.png"
                        {...stylex.props(imagePageStyles.heroBgImage)}
                        alt="ControlNet Pose Reference"
                    />
                    <div {...stylex.props(imagePageStyles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(imagePageStyles.heroContent)}>
                    <h2 {...stylex.props(imagePageStyles.heroTitle)}>CONTROLNET POSE REFERENCE</h2>
                    <p {...stylex.props(imagePageStyles.heroDescription)}>
                        Control human poses in AI-generated images with precision using <strong>skeleton keypoint detection</strong>. Generate consistent character poses for animation, game design, and creative projects.
                    </p>
                    <div {...stylex.props(imagePageStyles.heroStats)}>
                        <div {...stylex.props(imagePageStyles.stat)}>
                            <Target size={20} />
                            <span>Precise Pose Control</span>
                        </div>
                        <div {...stylex.props(imagePageStyles.stat)}>
                            <Zap size={20} />
                            <span>18+ Keypoint Detection</span>
                        </div>
                        <div {...stylex.props(imagePageStyles.stat)}>
                            <Users size={20} />
                            <span>Multi-Person Support</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div {...stylex.props(controlNetStyles.contentSection)}>

                {/* What is ControlNet */}
                <section {...stylex.props(controlNetStyles.featuresSection)}>
                    <h2 {...stylex.props(controlNetStyles.sectionTitle)}>What is ControlNet?</h2>
                    <p {...stylex.props(controlNetStyles.contentList)}>
                        ControlNet is an advanced neural network technique that gives you precise control over AI image generation by using additional conditioning inputs.
                        When combined with pose detection (OpenPose), it allows you to guide the exact positioning of human bodies, hands, and faces in your generated images.
                    </p>

                    <div {...stylex.props(controlNetStyles.featuresGrid)}>
                        <div {...stylex.props(controlNetStyles.featureCard)}>
                            <div {...stylex.props(controlNetStyles.featureIcon)}>
                                <Target size={24} />
                            </div>
                            <h4>Skeleton Keypoints</h4>
                            <p>Extract 18+ body keypoints including head, shoulders, elbows, wrists, hips, knees, and ankles for precise pose mapping</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.featureCard)}>
                            <div {...stylex.props(controlNetStyles.featureIcon)}>
                                <Zap size={24} />
                            </div>
                            <h4>Fast & Accurate</h4>
                            <p>Real-time pose detection that accurately reproduces poses without copying other details like outfits or backgrounds</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.featureCard)}>
                            <div {...stylex.props(controlNetStyles.featureIcon)}>
                                <Users size={24} />
                            </div>
                            <h4>Multi-Person Detection</h4>
                            <p>Simultaneously detect and control poses for multiple people in a single image</p>
                        </div>
                    </div>
                </section>

                <hr {...stylex.props(controlNetStyles.divider)} />

                {/* How it Works */}
                <section {...stylex.props(controlNetStyles.gettingStartedSection)}>
                    <h2 {...stylex.props(controlNetStyles.sectionTitle)}>How ControlNet Pose Works</h2>
                    <div {...stylex.props(controlNetStyles.gettingStartedContent)}>
                        <div {...stylex.props(controlNetStyles.stepCard)}>
                            <div {...stylex.props(controlNetStyles.stepNumber)}>1</div>
                            <h4>Upload Pose Reference</h4>
                            <p>Provide a skeleton pose image (stick figure or keypoints format) or use an existing photo to extract the pose</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.stepCard)}>
                            <div {...stylex.props(controlNetStyles.stepNumber)}>2</div>
                            <h4>Keypoint Extraction</h4>
                            <p>OpenPose detects and maps key body positions, creating a control map with precise joint locations</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.stepCard)}>
                            <div {...stylex.props(controlNetStyles.stepNumber)}>3</div>
                            <h4>Generate with Control</h4>
                            <p>Combine your text prompt with the pose control map to generate images that match your exact pose requirements</p>
                        </div>
                    </div>
                </section>

                <hr {...stylex.props(controlNetStyles.divider)} />

                {/* Test Results */}
                <h2 {...stylex.props(controlNetStyles.sectionTitle)}>Pose Accuracy Tests</h2>
                <p {...stylex.props(controlNetStyles.contentList)}>
                    We tested Qwen's ControlNet implementation across various pose complexities to measure accuracy and usability for real-world creative workflows.
                    Results show excellent performance for animation, character design, and game development.
                </p>

                {/* Test 1 */}
                <div {...stylex.props(controlNetStyles.testSection)}>
                    <div {...stylex.props(controlNetStyles.testHeader)}>
                        <span {...stylex.props(controlNetStyles.testBadge)}>Test 1</span>
                        <h3 {...stylex.props(controlNetStyles.testTitle)}>Simple Standing Pose</h3>
                    </div>

                    <div {...stylex.props(controlNetStyles.testMeta)}>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Complexity</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Basic standing pose with arm raised</div>
                        </div>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Use Case</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Character design, portrait generation</div>
                        </div>
                    </div>

                    <div {...stylex.props(controlNetStyles.promptSection)}>
                        <div {...stylex.props(controlNetStyles.promptLabel)}>Prompt</div>
                        <pre {...stylex.props(controlNetStyles.codeBlock)}>Use it as a reference, A woman in the same position</pre>
                    </div>

                    <div {...stylex.props(controlNetStyles.imageGrid)}>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test1-reference.png"
                                alt="Test 1 Reference Pose"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Reference Pose</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test1-generated.png"
                                alt="Test 1 Generated Image"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Generated Result</p>
                        </div>
                    </div>
                </div>

                {/* Test 2 */}
                <div {...stylex.props(controlNetStyles.testSection)}>
                    <div {...stylex.props(controlNetStyles.testHeader)}>
                        <span {...stylex.props(controlNetStyles.testBadge)}>Test 2</span>
                        <h3 {...stylex.props(controlNetStyles.testTitle)}>Dynamic Action Pose</h3>
                    </div>

                    <div {...stylex.props(controlNetStyles.testMeta)}>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Complexity</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Semi-fight stance with weapon positioning</div>
                        </div>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Use Case</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Action scenes, game character design</div>
                        </div>
                    </div>

                    <div {...stylex.props(controlNetStyles.promptSection)}>
                        <div {...stylex.props(controlNetStyles.promptLabel)}>Prompt</div>
                        <pre {...stylex.props(controlNetStyles.codeBlock)}>an anime style movie scene samurai in a fight position as reference</pre>
                    </div>

                    <div {...stylex.props(controlNetStyles.imageGrid)}>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test2-reference.webp"
                                alt="Test 2 Reference Pose"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Reference Pose</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test2-generated.jpg"
                                alt="Test 2 Generated Image"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Generated Result</p>
                        </div>
                    </div>
                </div>

                {/* Test 3 */}
                <div {...stylex.props(controlNetStyles.testSection)}>
                    <div {...stylex.props(controlNetStyles.testHeader)}>
                        <span {...stylex.props(controlNetStyles.testBadge)}>Test 3</span>
                        <h3 {...stylex.props(controlNetStyles.testTitle)}>Multi-Person with Overlapping Limbs</h3>
                    </div>

                    <div {...stylex.props(controlNetStyles.testMeta)}>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Complexity</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Two people with overlapping arms</div>
                        </div>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Use Case</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Group photos, social media content</div>
                        </div>
                    </div>

                    <div {...stylex.props(controlNetStyles.promptSection)}>
                        <div {...stylex.props(controlNetStyles.promptLabel)}>Prompt</div>
                        <pre {...stylex.props(controlNetStyles.codeBlock)}>Use the reference pose to create a photo of 2 friends taking a photo, make sure of the hands positions</pre>
                    </div>

                    <div {...stylex.props(controlNetStyles.imageGrid, controlNetStyles.threeColumns)}>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test3-reference.webp"
                                alt="Test 3 Reference Pose"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Reference Pose</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test3-generated1.jpg"
                                alt="Test 3 Generated Image 1"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Generated Result 1</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test3-generated2.jpg"
                                alt="Test 3 Generated Image 2"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Generated Result 2</p>
                        </div>
                    </div>
                </div>

                {/* Test 4 */}
                <div {...stylex.props(controlNetStyles.testSection)}>
                    <div {...stylex.props(controlNetStyles.testHeader)}>
                        <span {...stylex.props(controlNetStyles.testBadge)}>Test 4</span>
                        <h3 {...stylex.props(controlNetStyles.testTitle)}>Complex Multi-Directional Limbs</h3>
                    </div>

                    <div {...stylex.props(controlNetStyles.testMeta)}>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Complexity</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Arms and legs in different directions</div>
                        </div>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Use Case</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Fitness/yoga, athletic poses</div>
                        </div>
                    </div>

                    <div {...stylex.props(controlNetStyles.promptSection)}>
                        <div {...stylex.props(controlNetStyles.promptLabel)}>Prompt</div>
                        <pre {...stylex.props(controlNetStyles.codeBlock)}>visualize in the same position a woman in a gym, with a training suit</pre>
                    </div>

                    <div {...stylex.props(controlNetStyles.imageGrid)}>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test4-reference.png"
                                alt="Test 4 Reference Pose"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Reference Pose</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test4-generated.png"
                                alt="Test 4 Generated Image"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Generated Result</p>
                        </div>
                    </div>
                </div>

                {/* Test 5 */}
                <div {...stylex.props(controlNetStyles.testSection)}>
                    <div {...stylex.props(controlNetStyles.testHeader)}>
                        <span {...stylex.props(controlNetStyles.testBadge)}>Test 5</span>
                        <h3 {...stylex.props(controlNetStyles.testTitle)}>Portrait with Facial Structure</h3>
                    </div>

                    <div {...stylex.props(controlNetStyles.testMeta)}>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Complexity</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Upright pose with facial details</div>
                        </div>
                        <div {...stylex.props(controlNetStyles.metaItem)}>
                            <div {...stylex.props(controlNetStyles.metaLabel)}>Use Case</div>
                            <div {...stylex.props(controlNetStyles.metaValue)}>Portrait photography, headshots</div>
                        </div>
                    </div>

                    <div {...stylex.props(controlNetStyles.promptSection)}>
                        <div {...stylex.props(controlNetStyles.promptLabel)}>Prompt</div>
                        <pre {...stylex.props(controlNetStyles.codeBlock)}>Visualize as a man portrait</pre>
                    </div>

                    <div {...stylex.props(controlNetStyles.imageGrid)}>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test5-reference.png"
                                alt="Test 5 Reference Pose"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Reference Pose</p>
                        </div>
                        <div {...stylex.props(controlNetStyles.imageContainer)}>
                            <img
                                src="https://cdn.aibrush.co/docs/images/controlnet/test5-generated.jpg"
                                alt="Test 5 Generated Image"
                                {...stylex.props(controlNetStyles.image)}
                            />
                            <p {...stylex.props(controlNetStyles.imageLabel)}>Generated Result</p>
                        </div>
                    </div>
                </div>

                <hr {...stylex.props(controlNetStyles.divider)} />

                {/* Best Practices */}
                <section {...stylex.props(controlNetStyles.featuresSection)}>
                    <h2 {...stylex.props(controlNetStyles.sectionTitle)}>Best Practices & Tips</h2>

                    <div {...stylex.props(controlNetStyles.variantsGrid)}>
                        <div {...stylex.props(controlNetStyles.variantCard, controlNetStyles.variantCardPrimary)}>
                            <div {...stylex.props(controlNetStyles.variantHeader)}>
                                <h4 {...stylex.props(controlNetStyles.variantTitle)}>✓ When ControlNet Works Best</h4>
                            </div>
                            <ul {...stylex.props(controlNetStyles.variantFeatures)}>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Clear, balanced poses with distinct keypoints</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Static or medium-dynamic poses (standing, walking, simple actions)</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Simple backgrounds that don't interfere with pose detection</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Detailed text prompts specifying clothing and environment</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Single or well-separated multiple subjects</li>
                            </ul>
                        </div>

                        <div {...stylex.props(controlNetStyles.variantCard)}>
                            <div {...stylex.props(controlNetStyles.variantHeader)}>
                                <h4 {...stylex.props(controlNetStyles.variantTitle)}>⚠ Limitations to Consider</h4>
                            </div>
                            <ul {...stylex.props(controlNetStyles.variantFeatures)}>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Accuracy drops with heavily overlapping limbs</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Extreme foreshortening may be less precise</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Very complex multi-person scenes (3+ people) may need iteration</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>Unusual or abstract poses may be harder to reproduce</li>
                                <li {...stylex.props(controlNetStyles.variantFeatureItem)}>To get the image without text, simply include (without text) in the prompt</li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    )
}
